import { Epic, ofType } from "redux-observable";
import { AppActions } from "../actions";
import {
  adminRoleType,
  AppState,
  CompanyRole,
  CurrentSelectedCompanyPermissions,
  NoAdminRole,
  PortalAdmin, SiteAdminRole, LocationAdmin
} from "../AppState";
import { mergeMap, switchMap } from "rxjs/operators";
import { fromFetch } from "rxjs/fetch";
import ApiUrl from "../api/ApiUrl";
import { BuildAuthRequestFromAppState } from "../authentication/BuildAuthRequestInterface";
import { COMPANY_SELECTED, COMPANY_UPDATED, CompanySelectedAction, CompanySelectedPayload, CompanyUpdateAction, CompanyUpdated } from "../actions/WorkforceClinicAction";
import {
  WorkforceOneCompanyPermissionAction,
  WorkforceOneUserCompanyPermissionChange
} from "../actions/WorkforceOnePermissionActions";
import { EMPTY, of } from "rxjs";
import { ClinicStatus } from "../config/Constants";
import { setOrphanedResultsCount } from "../storage/LogInPortals";

interface ApiResponseModel {
  data: {
    IsClinic: boolean
    IsClient: boolean
    IsSiteAdmin: boolean
    IsLocation: boolean
    Status: number
    UserAccessViewModel: CurrentSelectedCompanyPermissions
  }
}
function MapRoleFromApiResponse(responseModel?: ApiResponseModel): adminRoleType {
  if (!responseModel) {
    return NoAdminRole;
  }
  if (responseModel.data.IsClinic) {
    return PortalAdmin;
  }
  if (responseModel.data.IsClient) {
    return CompanyRole;
  }
  if (responseModel.data.IsSiteAdmin) {
    return SiteAdminRole;
  }
  if (responseModel.data.IsLocation) {
    return LocationAdmin
  }
  return NoAdminRole;
}
const defaultCompanyPerms: CurrentSelectedCompanyPermissions = {
  CanShowClinicAdminSetup: false,
  CanShowInvitation: false,
  CanShowRolePermissionSubscription: false,
  CanShowNotification: false,
  CanShowClientInvitationStatus: false,
  CanShowUserInvitationStatus: false,
  CanShowPortal: false,
  CanShowInvitationStatus: false,
  CanViewSiteSearch: false,
  CanViewOrder: false,
  CanViewTracking: false,
  CanViewCompleted: false,
  CanInviteClientUser: false,
  CanManageClient: false,
  CanCreateClient: false,
  CanCreateInstance: false,
  CanEditPendingOrders: false,
  CanViewRandomTesting: false,
  CanManageClientUser: false,
  CanManageInstanceUser: false,
  CanViewPersonnel: false,
  CanViewOrderTrackingGrid: false,
  CanManageServiceEvent: false,
  CanVieweCCFCopy2PreMRO: false,
  CanViewOrderSummary: false,
  CanManagePersonnel: false,
  CanManagePools: false,
  CanCompleteEvents: false,
  CanUploadImages: false,
  CanViewAddressAlert: false,
  CanSearchDefaults: false,
  CanGroupInclude: false,
  CanManageOrphanResults: false,
  CanEditPersonnel: false,
  CanManageSelectionInbox: false,
  CanManageGovDonors: false,
  CanManageGovPools: false,
  CanManageConsortiumPools: false,
  CanOrderGovServices: false,
  CanViewMISReports: false,
  CanMergeTests: false,
  CanMatchTests: false
};
function MapApiResponseToAction(apiResponse?: ApiResponseModel, selectedCompany?: CompanySelectedPayload, requireCompleteSetup?: boolean): WorkforceOneCompanyPermissionAction {
    processResponseData(apiResponse?.data);
    const adminRoleType = MapRoleFromApiResponse(apiResponse);
    const companyResponse = apiResponse?.data.UserAccessViewModel || defaultCompanyPerms;
    const link = selectedCompany?.Link
    return WorkforceOneUserCompanyPermissionChange(adminRoleType, companyResponse, selectedCompany?.participationId || "", !!requireCompleteSetup, link || "");
}

function processResponseData(data: any) {
    if (!data) {
        return;
    }
    setPortalLogoImageUrl(data.LogoImage)
    setImplementationType(data.ImplementationType)
  setOrphanedResultsCount(data.OrphanedResultsCount)

}
function setPortalLogoImageUrl(logoImage: string) {
  localStorage.removeItem("portalImage");
  if (!!logoImage) {
    localStorage.setItem("portalImage", logoImage);
  }


}
function setImplementationType(implementationType: string) {
  sessionStorage.removeItem("implementationType");
  if (!!implementationType) {
    sessionStorage.setItem("implementationType", implementationType);
  }
}

export const WorkforceOneUpdateUserPortalPermission: Epic<AppActions, AppActions, AppState> = (action$, states$) => {
  return action$.pipe(
    ofType(COMPANY_UPDATED),
    mergeMap((action: AppActions) => {
            const updateAction = action as CompanyUpdateAction
            if (!updateAction.payload.ForceReload) return EMPTY
            const authDetails = BuildAuthRequestFromAppState(states$.value);
            return fromFetch(`${ApiUrl()}FFOrderAccount/GetPortalsCompaniesData`,
                {
                  method: 'GET',
                  credentials: 'omit',
                  headers: new Headers({
                      'auth_token': authDetails.auth_token,
                      'IsMarketPlace': authDetails.IsMarketPlace,
                      'Agent': authDetails.Agent,
                      'api_version': authDetails.api_version,
                      'Accept': 'application/json',
                      'Content-Type': 'application/json',
                  }),
                })
                .pipe(switchMap(response => response.ok ?
                  response.json() :
                  Promise.resolve(null)))
                .pipe(mergeMap(json => {
                  if (!json?.data) {
                    return EMPTY
                  }
                  return of(CompanyUpdated(json.data))
                }))
        }
    )
  );
};

const WorkforceOneUserPortalPermission: Epic<AppActions, AppActions, AppState> = (action$, states$) => {
  return action$.pipe(
    ofType(COMPANY_SELECTED),
    mergeMap((action: AppActions) => {
      const selectedCompany = action as CompanySelectedAction;
      const authDetails = BuildAuthRequestFromAppState(states$.value);
      return fromFetch(`${ApiUrl()}FFOrderClinicStatus/GetStatus`,
        {
          method: 'POST',
          credentials: 'omit',
          body: JSON.stringify(selectedCompany.payload),
          headers: new Headers({
            'auth_token': authDetails?.auth_token,
            'IsMarketPlace': authDetails.IsMarketPlace,
            'Agent': authDetails.Agent,
            'api_version': authDetails.api_version,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }),
        })
        .pipe(switchMap(response => response.ok ?
          response.json() :
          Promise.resolve(null)))
        .pipe(mergeMap(json => {
          if (!json?.data) {
            return EMPTY
          }
          const requireCompleteSetup = !(json.data.IsSiteAdmin || json.data.Status === ClinicStatus.Completed || (json.data.IsClinic && json.data.Status === ClinicStatus.ClinicConfigured) || ((json.data.IsClient || json.data.IsLocation) && (json.data.Status === ClinicStatus.UserConfigured || json.data.Status === ClinicStatus.ClientConfigured)))
          return of(MapApiResponseToAction(json, selectedCompany.payload, requireCompleteSetup))
        }))
    }
    )
  );
};
export default WorkforceOneUserPortalPermission;
